import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import SkillsInput from '../common/SkillsInput';
import JobAidsInput from '../common/JobAidsInput';
import { Radio, RadioGroup, FormControlLabel,FormControl } from '@mui/material';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { useUser } from '../../context/UserContext';
import PageTabs from '../shared/PageTabs';
import { buildCourseTabs } from '../shared/tabConfigs';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';

import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material';
import {
  Upload,
  Close,
  Add,
  Visibility
} from '@mui/icons-material';
import { courseService } from '../../services/api';
import ContentSelectionModal from './ContentSelectionModal';
const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));

const PreviewImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '200px',
  objectFit: 'cover',
  borderRadius: '8px'
});

const modules = {
  toolbar: {
    container: [
      [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      //['link', 'image', 'video'],
      ['clean']
    ]
  },
  clipboard: {
    matchVisual: false
  }
};
const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video'
];

/**
 * CourseForm is a React component for creating and editing courses.
 * It displays a form with fields for entering course name, description, and cover image.
 * It also displays a list of existing contents and allows the user to add new contents.
 * The component handles submission of the form and updates the course data in the database.
 * It also handles loading of the course data when the component is mounted.
 * @param {string} id - The ID of the course to edit, if any.
 * @returns {React.ReactElement} The rendered form.
 */
const CourseForm = () => {
  const { id } = useParams();

  const { user } = useUser();
  const permissions = user?.permissions || [];

  const courseTabs = buildCourseTabs(id, { permissions: user?.permissions || [], roleId: user?.role_id });

  const navigate = useNavigate();
  const { openContentPlayer } = useContentPlayer();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [formData, setFormData] = useState({
    course_name: '',
    description: '',
    course_status: '1',
    tags: [],
    skills: [],
    // Removed 2 Nov 2026 (K21.2 pt 46.1 (e)): the files and the audience read from the page state, which only Add
    // content's old 'Create a course from this' passed; it now creates the course first and opens it here.
    selected_contents: [],
    content_ordered: '0',
    job_aids: [],
    audience_type: '3'       // 1=Internal,2=External,3=Both
  });
  const [preview, setPreview] = useState('');

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setFormData(prev => ({
          ...prev,
          cover_picture: file
        }));
        setPreview(URL.createObjectURL(file));
      }
    },
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1
  });

  useEffect(() => {
    if (id) {
      loadCourseData();
    }
  }, [id]);

  const loadCourseData = async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const { data } = await courseService.getCourseById(id);
      setFormData({
        course_name: data.record.name,
        description: data.record.description,
        course_status: data.record.status,
        tags: data.record.tags ? data.record.tags.split(',') : [],
        selected_contents: data.added_contents || [],
        content_ordered: data.record.content_ordered || '0',
        job_aids: data.record.job_aids || [],
        skills: data.record.skills || [],
        audience_type: data.record.audience_type ? String(data.record.audience_type) : '3'

      });
      if (data.record.cover_picture) {
        setPreview(`/uploads/courses/${data.record.cover_picture}`);
      }
    } catch (error) {
      console.error('Error loading course:', error);
      setLoadError(error?.response?.data?.message || error.message || 'Unable to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};
  //Validation
  const [errors, setErrors] = useState({});
  const validateForm = () => {
    const newErrors = {};

    // Course name validation
    if (!formData.course_name.trim()) {
      newErrors.course_name = 'Course name is required';
    }

    if (!stripHtml(formData.description).trim()) {
    newErrors.description = 'Course description is required';
  }

    // Contents validation
    if (!formData.selected_contents || formData.selected_contents.length === 0) {
      newErrors.contents = 'At least one content must be added to the course';
    }

    // Tags validation
    // if (!formData.tags || formData.tags.length === 0) {
    //   newErrors.tags = 'At least one tag is required';
    // }
    
    //if (!formData.cover_picture && !preview) {
    //  newErrors.cover_picture = 'Course cover image is required';
    //}

    if (!formData.audience_type) {
      newErrors.audience_type = 'Please select an audience';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const coursePayload = {
        ...formData,
        skills: JSON.stringify((formData.skills || []).map((skill) => skill && typeof skill === 'object' ? skill.id : skill)),
      };
      const response = id
        ? await courseService.updateCourse(id, coursePayload)
        : await courseService.createCourse(coursePayload);

      setSnackbar({
        open: true,
        message: 'Course saved successfully!',
        severity: 'success'
      });
      setTimeout(() => {
        navigate(`/course/settings/${response.data.courseId}`);
      }, 1000);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Error saving course';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [activeId, setActiveId] = useState(null);
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!active || !over || active.id === over.id) return;

    const oldIndex = formData.selected_contents.findIndex(
      item => item.cust_id.replace('|', '_') === active.id
    );
    const newIndex = formData.selected_contents.findIndex(
      item => item.cust_id.replace('|', '_') === over.id
    );

    const reordered = arrayMove(formData.selected_contents, oldIndex, newIndex);
    const orderedList = reordered.map(item => item.cust_id);

    //  First update the UI
    setFormData(prev => ({
      ...prev,
      selected_contents: reordered,
    }));

    // Then call the API to persist the new order
    try {
      await courseService.updateCourseContentOrder(id, orderedList);
      // console.log('Order updated');
    } catch (err) {
      console.error('Order update failed', err);
    }
  };

  const handlePreviewContent = () => {
    if (formData.selected_contents && formData.selected_contents.length > 0) {
      // Extract the content ID from the first content's custom ID
      const firstContent = formData.selected_contents[0];
      const contentId = firstContent.cust_id.split('|')[0];
      openContentPlayer(id, contentId, `/course/edit/${id}`);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {id && <PageTabs tabs={courseTabs} />}
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ mt: 7 }}
        >
          <MuiAlert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
        
        <Typography variant="h4" sx={{ mb: 4 }}>
          {id ? 'Edit Course' : 'Create New Course'}
        </Typography>
        {loadError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadCourseData}>Retry</Button>}>
              {loadError}
            </Alert>
          </Box>
        )}


        <form onSubmit={handleSubmit}>
          <FormSection>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Basic Information
            </Typography>
            <TextField
              fullWidth
              required
              label="Course Name"
              name="course_name"
              value={formData.course_name}
              onChange={handleChange}
              variant="outlined"
              sx={{ mb: 3 }}
              error={!!errors.course_name}
              helperText={errors.course_name}
            />

          </FormSection>
          <FormSection>
            <Typography variant="h6">
              Course Description <Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={(content) => setFormData(prev => ({
                ...prev,
                description: content
              }))}
              style={{ height: '200px', marginBottom: '70px' }}
              modules={modules}
              formats={formats}
            />
            {errors.description && (
          <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
            {errors.description}
          </Typography>
        )}
          </FormSection>
          {/*
            <Typography variant="h6" sx={{ mt: 3 }}>
              Cover Image
            </Typography>
            <DropzoneArea {...getRootProps()}>
              <input {...getInputProps()} />
              {preview ? (
                <Box position="relative">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreview('');
                      setFormData(prev => ({ ...prev, cover_picture: null }));
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'white',
                      '&:hover': { backgroundColor: 'white' }
                    }}
                  >
                    <Close />
                  </IconButton>
                  <PreviewImage src={preview} alt="Cover preview" />
                </Box>
              ) : (
                <Box>
                  <Upload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Drag and drop your cover image
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Supported formats: Images [.jpeg, .jpg, .png]
                  </Typography>
                </Box>
              )}
            </DropzoneArea>
            {errors.cover_picture && (
              <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                {errors.cover_picture}
              </Typography>
            )}
            */ }
          <FormSection>          
            <Typography variant="h6" sx={{ mt: 3 }}>
              Audience <Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <RadioGroup
              row
              name="audience_type"
              value={formData.audience_type}
              onChange={handleChange}
            >
              <FormControlLabel value="1" control={<Radio />} label="Internal" disabled={!!id}/>
              <FormControlLabel value="2" control={<Radio />} label="External" disabled={!!id}/>
              <FormControlLabel value="3" control={<Radio />} label="Both" disabled={!!id}/>
            </RadioGroup>
            {errors.audience_type && (
              <Typography color="error" variant="caption" display="block">
                {errors.audience_type}
              </Typography>
            )}          
          </FormSection>
          <FormSection>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Course Content
            </Typography>
            <Box sx={{ display: 'flex', 'justify-content': 'space-between', gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => setIsContentModalOpen(true)}
                sx={{ mb: 2, height: 36 }}
                size="medium"
              >
                Add Course Content
              </Button>

              {/* Only show this when editing (id exists) and there are contents */}
              {id && formData.selected_contents && formData.selected_contents.length > 0 && (
                <Button
                variant="outlined"
                color="primary"
                startIcon={<Visibility />}
                onClick={handlePreviewContent}
                size="medium"
                sx={{
                  height: 36,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                  },
                  '&:active': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                  }
                }}
              >
                Preview Content
              </Button>

              )}
            </Box>
            {errors.contents && (
              <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                {errors.contents}
              </Typography>
            )}
            {/* Add the modal component */}
            <ContentSelectionModal
              open={isContentModalOpen}
              onClose={() => setIsContentModalOpen(false)}
              courseId={id}
              audienceType={formData.audience_type}
              selectedContents={formData.selected_contents}
              onSelect={(content) => {
                // The new item shows its file type, as the course's saved items do (N14 pt 5.1): the picker's Subtype
                // column, which reads '-' when there is none. Removed 2 Nov 2026 (N14 pt 5.1): its Type column
                // (content[3], 'content'), which showed as CONTENT.
                const newContent = {
                  cust_id: `${content[0]}`,
                  title: content[1].split('<br/>')[0],
                  source_type: content[2] && content[2] !== '-' ? content[2] : ''
                };
                setFormData(prev => ({
                  ...prev,
                  selected_contents: [...prev.selected_contents, newContent]
                }));
              }}
            />

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={formData.selected_contents.map(content => content.cust_id.replace('|', '_'))}
                strategy={verticalListSortingStrategy}
              >
                {formData.selected_contents
                  .filter(content => content && content.cust_id)
                  .map((content) => (
                    <SortableItem
                      key={content.cust_id.replace('|', '_')}
                      id={content.cust_id.replace('|', '_')}
                      content={content}
                      onDelete={() => {
                        setFormData(prev => ({
                          ...prev,
                          selected_contents: prev.selected_contents.filter(item => item.cust_id !== content.cust_id)
                        }));
                      }}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </FormSection>
          <FormSection>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Box>
                <Typography variant="h6" gutterBottom>
                  Sequencing of Modules
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Learners must complete modules in order when enabled.
                </Typography>
              </Box>
              <RadioGroup
                row
                name="content_ordered"
                value={formData.content_ordered}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="0"
                  control={<Radio />}
                  label="Ordered"
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', color: 'text.primary' } }}
                />
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label="Unordered"
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', color: 'text.primary' } }}
                />
              </RadioGroup>
            </Box>
          </FormSection>
          {id && (
            <FormSection>
              <SkillsInput
                value={formData.skills}
                onChange={(newSkills) =>
                  setFormData((prev) => ({
                    ...prev,
                    skills: newSkills,
                  }))
                }
                error={!!errors.skills}
                helperText={errors.skills}
                required={false}
              />
            </FormSection>
          )}

          {/* <FormSection>
          <JobAidsInput
            value={formData.job_aids}
            onChange={(newJobAids) => setFormData(prev => ({
              ...prev,
              job_aids: newJobAids
            }))}
            error={!!errors.job_aids}
            helperText={errors.job_aids}
            required={false}
          />
        </FormSection> */}

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}
          >
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/courses')}
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              className="add-course-btn"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default CourseForm;